import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useNavigationType, Link, Navigate } from 'react-router-dom';
import { useScrollRestoration } from '@redpanda/utils';
import AccountListPage from './pages/AccountListPage';
import JournalListPage from './pages/JournalListPage';
import InvoiceManagePage from './pages/InvoiceManagePage';
import ReportsPage from './pages/ReportsPage';
import DashboardPage from './pages/DashboardPage';
import PeriodManagementPage from './pages/PeriodManagementPage';
import PeriodComparisonPage from './pages/PeriodComparisonPage';
import ProfilePage from './pages/ProfilePage';
import BankAccountPage from './pages/BankAccountPage';
import BankTransactionPage from './pages/BankTransactionPage';
import BankReconciliationPage from './pages/BankReconciliationPage';
import TaxReportPage from './pages/TaxReportPage';
import AdminPage from './pages/AdminPage';
import ManualPage from './pages/ManualPage';
import DataIOPage from './pages/DataIOPage';

import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { Language } from '@redpanda/types';
import { CustomSelect, FAQPage, TermsOfServicePage, PrivacyPolicyPage } from '@redpanda/ui';
import PrivateRoute from './components/PrivateRoute';
import { ConfirmModal } from '@redpanda/ui';
import Sidebar from './components/Sidebar';
import ThemeSelector from './components/ThemeSelector';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Bars3Icon } from '@heroicons/react/24/outline';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent: React.FC = () => {
  const { isAuthenticated, user, logout, isLoading, theme, updateUserTheme, updateUserLanguage } = useContext(UserContext)!;
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // 設定動態頁面標題
  useEffect(() => {
    document.title = `RedPanda ${t('app.title')}`;
  }, [t]);

  // 捲動管理（共用）：進新頁面置頂；重新整理、上一頁時還原捲動位置
  useScrollRestoration(location, useNavigationType());

  useEffect(() => {
    // 記錄最後活動頁面（排除手冊頁面本身）
    if (location.pathname !== '/manual') {
      sessionStorage.setItem('lastActivePage', location.pathname);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    setLogoutModalOpen(false);
    // 直接登出，不顯示載入畫面
    logout();
  };
  
  const changeLanguage = async (lng: string) => {
    if (updateUserLanguage) {
      await updateUserLanguage(lng as Language);
    } else {
      // Fallback for when user is not logged in
      i18n.changeLanguage(lng);
    }
  };


  const settingsMenuContent = (
    <>
      <Link
        to="/profile"
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-600"
      >
        個人資訊
      </Link>
      {(user?.role === 'SUPER_ADMIN') && (
        <Link
          to="/admin"
          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-600"
        >
          管理員
        </Link>
      )}
      <Link
        to="/manual"
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-600"
      >
        使用手冊
      </Link>
      <div className="border-t border-gray-200 dark:border-slate-600 my-2"></div>
      <div className="px-4 py-2">
        <ThemeSelector />
      </div>
      <div className="px-4 py-2">
        <p className="text-sm font-medium text-white dark:text-white mb-2">語言</p>
        <CustomSelect
          value={i18n.language}
          onChange={(value) => changeLanguage(value)}
          placeholder={t('common.placeholders.selectLanguage')}
          className="h-10"
          options={[
            { value: 'zh', label: t('common.languages.zh') },
            { value: 'en', label: t('common.languages.en') },
            { value: 'ja', label: t('common.languages.ja') }
          ]}
        />
      </div>
      <div className="border-t border-gray-200 dark:border-slate-600 my-2"></div>
      {isAuthenticated && (
        <button
          onClick={() => setLogoutModalOpen(true)}
          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-slate-600"
        >
          登出
        </button>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          {/* 品牌 LOGO - 放大到兩倍 */}
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-8xl mb-6">🐼</div>

          {/* 載入動畫 */}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`App bg-white dark:bg-slate-950 min-h-screen flex transition-colors duration-300`}>
      <Toaster position="bottom-right" />

      {/* 手機版漢堡按鈕 */}
      {isAuthenticated && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </button>
      )}

      {/* 左側導覽列 */}
      {isAuthenticated && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 主要內容區域 */}
      <div className={`flex-1 flex flex-col ${isAuthenticated ? 'md:ml-48' : ''}`}>

        {/* 主要內容 */}
        <main className="flex-grow">
          <Routes>
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/accounts" element={<AccountListPage />} />
              <Route path="/journals" element={<JournalListPage />} />
              <Route path="/invoices" element={<InvoiceManagePage />} />
              <Route path="/invoices/archived" element={<InvoiceManagePage />} />
              <Route path="/bank-accounts" element={<BankAccountPage />} />
              <Route path="/bank-accounts/:accountId" element={<BankTransactionPage />} />
              <Route path="/bank-reconciliation" element={<BankReconciliationPage />} />
              <Route path="/tax-reports" element={<TaxReportPage />} />
              <Route path="/period-management" element={<PeriodManagementPage />} />
              <Route path="/period-comparison" element={<PeriodComparisonPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/manual" element={<ManualPage />} />
              <Route path="/data-io" element={<DataIOPage />} />
            </Route>
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* 底部 */}
      </div>

      <ConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
        title={t('components.modal.logoutConfirm.title')}
        message="您確定要登出嗎？"
        confirmText="登出"
        cancelText="取消"
        type="warning"
        t={t}
      />
    </div>
  );
}

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ColorThemeProvider>
        <UserProvider>
          <AppContent />
        </UserProvider>
      </ColorThemeProvider>
    </Router>
  </QueryClientProvider>
);

export default App;